// Copyright (c) 2024-2026 Noushad Khalid / Codeteki. All rights reserved.
// Licensed under the Elastic License 2.0 — see LICENSE for details.

import { Link } from "wouter";
import { MessageCircle, Bot, ArrowRight, Zap, Globe, PhoneCall, Calculator, Users, Clock, Send, Sparkles, Wrench, ClipboardList, Package } from "lucide-react";

const PRODUCTS = [
  {
    href: "/ai-chat-widget",
    icon: MessageCircle,
    iconBg: "bg-yellow-50",
    iconColor: "text-yellow-500",
    borderColor: "border-yellow-400",
    badgeBg: "bg-yellow-50 text-yellow-800",
    badge: "Live Now",
    title: "AI Chat Widget",
    tagline: "Turn website visitors into leads — 24/7, on autopilot",
    description:
      "Embed a smart AI chatbot on your website in minutes. It answers questions, captures leads, and syncs with your knowledge base — so you never miss a prospect again.",
    bullets: [
      { icon: Zap, text: "Instant setup — no coding required" },
      { icon: Globe, text: "Works on any website or landing page" },
      { icon: Bot, text: "Trained on your own knowledge base" },
    ],
    ctaLabel: "See Plans & Pricing",
    ctaClass:
      "bg-gradient-to-r from-[#f9cb07] to-[#ffcd3c] hover:from-[#e6b800] hover:to-[#f9cb07] text-black",
    hasApp: true,
  },
  {
    href: "/instant-quote-widget",
    icon: Calculator,
    iconBg: "bg-green-50",
    iconColor: "text-green-600",
    borderColor: "border-green-500",
    badgeBg: "bg-green-50 text-green-800",
    badge: "New",
    title: "Instant Quote Widget",
    tagline: "Turn 'what's the price?' into a captured lead — instantly",
    description:
      "Embed an AI-powered quote calculator on your website. Visitors answer 3 questions and get a real price range — then submit their details to receive it. You get the lead, they get the quote.",
    bullets: [
      { icon: Calculator, text: "AI calculates price ranges from your context" },
      { icon: Users, text: "Captures name, email & phone before showing price" },
      { icon: Zap, text: "Works for any trades or service business" },
    ],
    ctaLabel: "See Plans & Pricing",
    ctaClass:
      "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white",
    hasApp: true,
  },
  {
    href: "/service-reminders",
    icon: Clock,
    iconBg: "bg-emerald-50",
    iconColor: "text-emerald-600",
    borderColor: "border-emerald-500",
    badgeBg: "bg-emerald-50 text-emerald-800",
    badge: "New",
    title: "Service Reminders",
    tagline: "Bring repeat customers back — automatically",
    description:
      "Automated rebooking-due reminder emails for any repeat-customer business — mechanics, salons, dentists, and more. Add your customer list once and every customer gets reminded when they're due back, right on schedule.",
    bullets: [
      { icon: Send, text: "3-stage reminder cadence, fully automated" },
      { icon: Sparkles, text: "AI-composed emails per service type" },
      { icon: Users, text: "Approval-based booking calendar built in" },
    ],
    ctaLabel: "See Plans & Pricing",
    ctaClass:
      "bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white",
    hasApp: true,
  },
  {
    href: "/spanner",
    icon: Wrench,
    iconBg: "bg-orange-50",
    iconColor: "text-orange-600",
    borderColor: "border-orange-500",
    badgeBg: "bg-orange-50 text-orange-800",
    badge: "New",
    title: "Spanner",
    tagline: "Workshop management, built for Australian mechanics",
    description:
      "Run your whole workshop from one dashboard — customers, vehicles, bookings, job cards, quotes, parts & stock, purchase orders, staff logins, and reporting, all self-serve.",
    bullets: [
      { icon: ClipboardList, text: "Job cards & invoicing from booking to paid" },
      { icon: Package, text: "Parts & stock adjust automatically" },
      { icon: Users, text: "Individual staff logins for every mechanic" },
    ],
    ctaLabel: "See Plans & Pricing",
    ctaClass:
      "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white",
    hasApp: true,
  },
  // HIDDEN: pending Meta app approval — re-enable when approved or switch to Twilio
  // {
  //   href: "/whatsapp-ai",
  //   icon: PhoneCall,
  //   iconBg: "bg-green-50",
  //   iconColor: "text-green-600",
  //   borderColor: "border-green-500",
  //   badgeBg: "bg-green-50 text-green-800",
  //   badge: "New",
  //   title: "WhatsApp AI Bot",
  //   tagline: "Reply to every WhatsApp inquiry instantly — even at 3am",
  //   description:
  //     "Give your business a dedicated Australian WhatsApp number powered by AI. It greets customers, answers FAQs, and alerts you the moment a real conversation starts.",
  //   bullets: [
  //     { icon: MessageCircle, text: "Dedicated AU WhatsApp Business number" },
  //     { icon: Zap, text: "Auto-replies from your knowledge base" },
  //     { icon: PhoneCall, text: "Owner alert on every new conversation" },
  //   ],
  //   ctaLabel: "See Plans & Pricing",
  //   ctaClass:
  //     "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white",
  // },
];

export default function ProductsHighlight() {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <span className="inline-block bg-yellow-50 text-yellow-800 text-xs font-semibold px-3 py-1 rounded-full mb-3 tracking-wide uppercase">
            AI Products
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
            Four products. One goal — grow your business on autopilot.
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto text-lg">
            Pick the tool that fits how your customers enquire and return, or use them together.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {PRODUCTS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.href}
                className={`relative flex flex-col rounded-2xl border-2 ${p.borderColor} bg-white shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden`}
              >
                <div className="p-8 flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`${p.iconBg} rounded-xl p-3`}>
                      <Icon className={`w-6 h-6 ${p.iconColor}`} />
                    </div>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${p.badgeBg}`}>
                      {p.badge}
                    </span>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{p.title}</h3>
                  <p className="text-sm font-semibold text-gray-500 mb-4">{p.tagline}</p>
                  <p className="text-gray-600 mb-6 leading-relaxed">{p.description}</p>

                  <ul className="space-y-2 mb-8">
                    {p.bullets.map(({ icon: BulletIcon, text }) => (
                      <li key={text} className="flex items-center gap-2 text-sm text-gray-700">
                        <BulletIcon className="w-4 h-4 text-gray-400 shrink-0" />
                        {text}
                      </li>
                    ))}
                  </ul>

                  <Link href={p.href}>
                    <button
                      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 hover:shadow-md ${p.ctaClass}`}
                    >
                      {p.ctaLabel}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </Link>

                  {p.hasApp && (
                    <div className="flex items-center gap-2 mt-3 text-gray-400" title="Also in the Codeteki mobile app">
                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M3.6 2.3c-.4.3-.6.8-.6 1.4v16.6c0 .6.2 1.1.6 1.4l.1.1L13 12.5v-.1L3.7 2.2l-.1.1z"/>
                        <path fill="currentColor" d="M16.1 15.6l-3.1-3.1v-.1l3.1-3.1 6.9 4c1 .5 1 1.3 0 1.9l-6.9 4.4zM16.1 15.6L13 12.5 3.6 21.9c.35.36.9.4 1.6.05l10.9-6.35zM16.1 9.4L5.2 3.05c-.7-.35-1.25-.3-1.6.05L13 12.5l3.1-3.1z"/>
                      </svg>
                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.744-.85 2.037-1.52 3.107-1.55.03.1.03.2.03.3zm4.607 16.44c-.28.62-.4 1.02-.75 1.65-.5.9-1.19 2.03-2.05 2.04-.77.01-.97-.5-2.01-.5-1.04 0-1.28.49-2.04.51-.86.03-1.5-.98-2-1.87-1.4-2.5-1.65-5.44-.73-7.15.65-1.2 1.83-1.96 3.1-1.98.79-.02 1.53.53 2.01.53.48 0 1.39-.66 2.34-.56.4.02 1.5.16 2.22 1.2-.06.04-1.32.77-1.31 2.3.02 1.83 1.6 2.44 1.62 2.45.02.04-.32 1.4-.42 1.72z"/>
                      </svg>
                      <span className="text-[11px] font-semibold">Also in our mobile app</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
